// src/utils/cleanText.js

// --- Simple, safe replacements (run first) ---
const REPLACEMENTS = [
  // HTML tags / primitives (safe, whole-word)
  [/\bdiv\b/gi, 'section'],
  [/\bspan\b/gi, 'text'],
  [/\bhref\b/gi, 'link'],
  [/\bimg\b/gi, 'image'],
  [/\binput\b/gi, 'form field'],
  [/\btextarea\b/gi, 'message box'],
  [/\bselect\b/gi, 'dropdown'],
  [/\bbtn\b/gi, 'button'],
  [/\bbutton\b/gi, 'button'],

  // Layout terms
  [/\b(container|wrapper|ui[-\s]?card)\b/gi, 'section'],

  // UI jargon
  [/\bmodal\b/gi, 'pop-up'],
  [/\btooltip\b/gi, 'info tip'],
  [/\bcarousel\b/gi, 'image slider'],
  [/\bhero\b/gi, 'banner'],
  [/\bnav\b/gi, 'menu'],
  [/\bbreadcrumbs?\b/gi, 'path links'],

  // Analytics jargon
  [/\bscroll depth\b/gi, 'how far down the page someone scrolled'],
  [/\bbounce rate\b/gi, 'share of people who left after one page'],
  
  // Headings / fluff to drop
  [/^\s*TL;DR:?/i, ''],
  [/^\s*Quick summary:?/i, ''],

  // ❌ REMOVED MARKDOWN STRIPPING - Let React components handle it
  // [/\*{1,3}(.*?)\*{1,3}/g, '$1'], // <-- DELETE THIS LINE
  // [/_{1,3}(.*?)_{1,3}/g, '$1'], // <-- DELETE THIS LINE
  // [/`([^`]+)`/g, '$1'], // <-- DELETE THIS LINE TOO

  // Cosmetic tidy-ups
  [/\s{2,}/g, ' '], // collapse extra spaces
  [/\s+([.,;:!?])/g, '$1'], // trim space before punctuation
  
  // Markdown headers and links
  [/^#+\s+/gm, ''],
  [/\[(.*?)\]\([^)]+\)/g, '$1'],

  // Analytics & e-comm jargon
  [/\bconversion rate\b/gi, 'percentage of visitors who bought'],
  [/\bfunnel\b/gi, 'customer journey'],
  [/\btouchpoints?\b/gi, 'customer interactions'],
  [/\bsessions?\b/gi, 'visits'],
  [/\bpageviews?\b/gi, 'pages viewed'],
  [/\bunique visitors?\b/gi, 'individual visitors'],
  [/\bCTR\b/g, 'click rate'],
  [/\bAOV\b/g, 'average order value'],
  [/\bGMV\b/g, 'total sales value'],
  [/\bROI\b/g, 'return on investment'],
  [/\bKPI\b/g, 'key metric'],
  [/\bproduct listing page\b/gi, 'product catalog'],
  [/\bPLP\b/g, 'product catalog'],
  [/\bPDP\b/g, 'product page'],
  [/\bupsell\b/gi, 'additional suggestions'],
  [/\bcross-sell\b/gi, 'related products'],
  [/\bcheckout flow\b/gi, 'purchase process'],
  [/\bcart abandonment\b/gi, 'left without buying'],
];

// Add a new function for stripping markdown when needed (for UI elements only)
export function cleanTextNoMarkdown(str = '') {
  let text = cleanText(str); // Apply normal cleaning first
  
  // Then strip markdown for UI elements
  text = text
    .replace(/\*{1,3}(.*?)\*{1,3}/g, '$1') // **bold**
    .replace(/_{1,3}(.*?)_{1,3}/g, '$1')   // _italic_
    .replace(/`([^`]+)`/g, '$1');          // `code`
  
  return text;
}

// --- Smart text pipeline (shared) ---
export const REPLACEMENTS_EX = [
  // Canonicalize CTA(s) and call to action(s) → "Call-To-Action(s)"
  [/\bCTA(s?)\b/gi, (_, s) => (s ? 'Call-To-Actions' : 'Call-To-Action')],
  [/\bcall[-\s]?to[-\s]?action(s?)\b/gi, (_, s) => (s ? 'Call-To-Actions' : 'Call-To-Action')],

  // General UI tokens
  [/\b(call-to-action)\s*\((?:call-to-action)\)/gi, '$1'],
  [/\b(?:DIV|SPAN|SECTION|CONTAINER|WRAPPER|CARD)\b/gi, 'section'],
  [/\b(?:INPUT|TEXTAREA|SELECT|FIELD|FORM[-\s]?FIELD)\b/gi, 'form field'],
  [/\b(?:BUTTON|BTN)\b/gi, 'button'],
  [/\b(?:IMG|IMAGE|PICTURE|THUMBNAIL)\b/gi, 'image'],
  [/\b(?:ANCHOR|HYPERLINK)\b/gi, 'link'], // do NOT replace bare "A" or "link"
  [/\b(?:MODAL|DIALOG|POPOVER|TOOLTIP)\b/gi, 'popup'],
  [/\b(?:NAV|NAVBAR|MENU|MEGAMENU)\b/gi, 'menu'],
  [/\b(?:CAROUSEL|SLIDER)\b/gi, 'carousel'],
  [/\bHERO\b/gi, 'banner'],

  // Abbreviations & punctuation
  [/\beg\.?,/gi, 'e.g.,'],
  [/\bi\.e\.?,/gi, 'i.e.,'],
  [/\.{2,}/g, '.'], // collapse ellipses to one period
  [/\s+([.,;:!?])/g, '$1'], // trim space before punctuation

  // Units/percents spacing
  [/\s+%/g, '%'],
  [/\b(\d+)\s?mins?\b/gi, '$1 minutes'],
  [/\b(\d+)\s?hrs?\b/gi, '$1 hours'],

  // Stray generator artifacts
  [/^\s*(?:link|href)[: ]?/gim, ''], // remove leading "link"/"href:"
];

export function cleanText(str = '') {
  let text = String(str);

  // Old step 1: remove obvious garbage
  text = removeGarbage(text);

  // Old step 2: core replacements
  const all = [...(REPLACEMENTS || []), ...REPLACEMENTS_EX];
  all.forEach(([re, rep]) => { text = text.replace(re, rep); });

  // Old step 3–4: number/percent normalization the old file did
  text = normalizePercentDecimals(text);
  text = normalizeAndClampRates(text);

  // Old step 5: CTA normalization
  text = normalizeCTA(text);

  // Old step 6: final tidy (punctuation/casing)
  text = finalCleanup(text);

  return text.trim();
}

// Safer formatPageLabel for SSR/build (no window dependency)
const _safeOrigin = typeof window !== 'undefined' && window.location ? window.location.origin : 'http://example.com';

export function formatPageLabel(path = '') {
  if (!path || path === '/' || path === '') return 'Homepage';
  let p = path;
  try {
    p = new URL(path, _safeOrigin).pathname;
  } catch {}
  p = decodeURIComponent(p)
    .replace(/\/index(\.html?)?$/i, '/') // /index.html → /
    .replace(/\/$/, ''); // trim trailing slash

  if (!p || p === '/') return 'Homepage';

  // Use the last non-empty segment, Title Case it
  const seg = p.replace(/^\//, '').split('/').filter(Boolean).pop() || '';
  const pretty = seg
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

  return pretty || 'Homepage';
}

export function isCitationLine(line = '') {
  const t = String(line).trim();
  return (
    /^source\s*:/i.test(t) ||
    /^references?\s*:/i.test(t) ||
    /^https?:\/\//i.test(t) ||
    /\bnielsen\s+norman\s+group\b/i.test(t)
  );
}

export function isGarbageLine(line = '') {
  const t = line.trim().toLowerCase();
  return !t || t === 'n/a' || t === 'none' || t === '-' || t === 'ÃƒÂ¢Ã¢â€šÂ¬"';
}

export function humanizeToken(token) {
  return token.replace(/^[#.]/, '').replace(/[_-]+/g, ' ').replace(/\s{2,}/g, ' ').trim();
}
 
export function sanitizeLine(line) {
  let t = String(line || '');
  t = t.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '');
  t = t.replace(/^\s*(?:[>*\-–—•·▪▫◦‣⁃]|\d+[.)]|&bull;|&middot;|&#8226;|&#x2022;)\s+/g, '');
  //t = t.replace(/\*/g, '');
  t = t.replace(/<\/?([^>]+)>/g, '');
  t = t.replace(/([#.][A-Za-z0-9_-]+)/g, (_, m) => humanizeToken(m));
  REPLACEMENTS_EX.forEach(([re, val]) => {
    t = t.replace(re, val);
  });
  t = t.replace(/\b([a-z][a-z-]*(?:\s+[a-z][a-z-]*)?)\s*\(\s*\1\s*\)/gi, '$1');
  t = t.replace(/\s{2,}/g, ' ').trim();
  if (t) t = t.charAt(0).toUpperCase() + t.slice(1);
  return t;
}

export function normalizeTimeToAction(line) {
  if (!line) return line;
  const m = line.match(/time to action[^:]*:\s*.*?(\d+(?:\.\d+)?)\s*second/i);
  if (!m) return line;
  const sec = parseFloat(m[1]);
  let verdict = 'which is within a normal range.';
  if (sec <= 2) verdict = 'indicating users act very quickly (could be clear Call-To-Actions, accidental taps, or bots).';
  else if (sec >= 15) verdict = 'indicating friction before acting (confusing UI, slow load, or hesitation).';
  else if (sec >= 8) verdict = 'suggesting some hesitation before acting.';
  return line.replace(/suggesting.*$/i, verdict).replace(/which indicates.*$/i, verdict);
}

export function normalizePercentDecimals(line) {
  return line
    .replace(/(^|\s)\.(\d{1,3})(?=[^\n]{0,20}\b(?:rate|conversion|ctr|engagement)\b)/gi, (_, sp, d) => `${sp}0.${d}`)
    .replace(/\b(0\.\d{1,3})(?!\s*%)(?=[^\n]{0,20}\b(?:rate|conversion|ctr|engagement)\b)/gi,
      (_, d) => `${Math.round(parseFloat(d) * 100)}%`);
}

export function normalizeAndClampRates(line) {
  line = line.replace(
    /\b((?:exit|bounce|engagement|conversion)\s*rate[^:]*:\s*)(\d+(?:\.\d+)?)(\s*%?)/gi,
    (_m, prefix, raw) => {
      let n = parseFloat(raw.replace(/,/g, ''));
      if (!isFinite(n)) return _m;
      if (n > 0 && n <= 1) n *= 100;
      if (n > 100) n = 100;
      return `${prefix}${n.toFixed(n % 1 ? 1 : 0)}%`;
    }
  );
  line = line.replace(/(\d+(?:\.\d+)?)\s*%/g, (_m, raw) => {
    let n = parseFloat(raw);
    if (!isFinite(n)) return _m;
    if (n > 100) n = 100;
    return `${n.toFixed(n % 1 ? 1 : 0)}%`;
  });
  return line;
}

export function dollars(n) {
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatNumbersAndMoney(line) {
  let t = line.replace(/\b\d{4,}(\.\d+)?\b/g, (m) => {
    const n = Number(m);
    if (n >= 1900 && n <= 2099 && Number.isInteger(n)) return m; // years
    return n.toLocaleString();
  });
  t = t.replace(/\b(revenue|sales|aov|average order value|gmv)[^$0-9]{0,10}\$?(\d[\d,]*(?:\.\d+)?)/gi,
    (_, label, num) => `${label} $${dollars(num.replace(/,/g, ''))}`);
  return t;
}

export function emphasizeMetrics(line) {
  return line
    .replace(/\b\d+(?:\.\d+)?\s*(?:%|percent|seconds?|sec|minutes?|mins?|ms|hours?|hrs?)\b/gi, m => `<strong>${m}</strong>`)
    .replace(/\b\d{1,3}(?:,\d{3})+(?:\.\d+)?\b/g, m => `<strong>${m}</strong>`)
    .replace(/\b(\$)\s*\d[\d,]*(?:\.\d+)?\b/g, m => `<strong>${m}</strong>`)
    .replace(/\b(bounce rate|conversion rate|scroll depth|avg(?:erage)? time(?: on page)?|aov|gmv|sessions?|orders?|clicks?)\b/gi, m => `<strong>${m}</strong>`);
}

export function allowOnlyStrong(html = '') {
  return html.replace(/<(?!\/?strong\b)[^>]*>/gi, '');
}
export function endWithPeriod(line) {
  return /[.!?]$/.test(line) ? line : `${line}.`;
}

// 0..1 → 0..100 (or pass-through if already 0..100)
export function percent01To100(x) {
  const n = Number(x);
  if (!isFinite(n)) return null;
  return Math.max(0, Math.min(100, n <= 1 ? n * 100 : n));
}

// One-call prettifier used by the UIs
export function toReadableHTML(line) {
  if (!line || isCitationLine(line) || isGarbageLine(line)) return '';
  let t = sanitizeLine(line);
  t = normalizeTimeToAction(t);
  t = normalizePercentDecimals(t);
  t = normalizeAndClampRates(t);
  t = formatNumbersAndMoney(t);
  t = emphasizeMetrics(t);
  t = endWithPeriod(t);
  t = allowOnlyStrong(t);
  return t;
}

// Robust section extractor for Insights text blobs
// Robust section extractor for Insights text blobs
export function extractSections(raw = '') {
  if (!raw) return {
    primaryText: '',
    keyInsightText: '',
    supportingSignalsText: '',
    recommendationsText: ''
  };

  // Remove everything after "Action Plan" or "Next Steps" FIRST
  let cleanedRaw = raw;
  const actionPlanIndex = raw.search(/\n\s*(?:Action Plan|Next Steps)[\s:/]/i);
  if (actionPlanIndex !== -1) {
    cleanedRaw = raw.substring(0, actionPlanIndex);
  }

  // NEW: Extract the primary revenue opportunity statement (the bold part)
 // Extract the primary headline (revenue opportunity OR conversion blocker)
// Handles optional "this week", newline before the bold line, etc.
const primary = cleanedRaw.match(
  /The primary (?:revenue opportunity|conversion blocker)(?: this week)? is:\s*\*\*(.+?)\*\*/is
);


  // Extract "Key Insight:" section
  const key = cleanedRaw.match(
    /Key Insight:\s*([\s\S]*?)(?=\n\s*(?:What We Found|Supporting Signals|Why It Matters|Recommendation)\s*:)/i
  );

  // NEW: Extract "What We Found:" (your rule-based insights use this)
  const whatWeFound = cleanedRaw.match(
    /What We Found:\s*([\s\S]*?)(?=\n\s*(?:Why It Matters|Recommendation)\s*:)/i
  );

  // NEW: Extract "Why It Matters:" (alternative key insight location)
  const whyItMatters = cleanedRaw.match(
    /Why It Matters:\s*([\s\S]*?)(?=\n\s*(?:Recommendation)\s*:)/i
  );

  // OLD: Keep backward compatibility with "Supporting Signals"
  const sig = cleanedRaw.match(
    /(?:Supporting Signals|Signals|Findings):\s*([\s\S]*?)(?=\n\s*(?:Recommendation\s*1|Recommendations?|What You Should Do)\s*:)/i
  );

  // Extract recommendations (handles "Recommendation 1:", "Recommendations:", etc.)
  const rec = cleanedRaw.match(
    /(?:Recommendation\s*1:|Recommendations:|What You Should Do:)\s*([\s\S]*?)$/i
  );

  // Fallback: if no sections found, split by double newlines
  if (!key && !sig && !rec && !whatWeFound && !whyItMatters) {
    const parts = cleanedRaw.split(/\n\s*\n/).map(s => s.trim());
    return {
      primaryText: primary ? primary[1].trim() : '',
      keyInsightText: parts[0] || '',
      supportingSignalsText: parts[1] || '',
      recommendationsText: parts.slice(2).join('\n\n') || ''
    };
  }

  return {
    primaryText: primary ? primary[1].trim() : '',
    keyInsightText: whyItMatters ? whyItMatters[1].trim() : (key ? key[1].trim() : ''),
    supportingSignalsText: whatWeFound ? whatWeFound[1].trim() : (sig ? sig[1].trim() : ''),
    recommendationsText: rec ? rec[1].trim() : ''
  };
}

export function isTooVague(line = '') {
  const t = String(line || '').toLowerCase();
  const hasNumberOrPercent = /\d/.test(t) || /%|seconds?|mins?|minutes?|hours?|hrs?/i.test(t);
  const hasContextWord =
    /(cart|checkout|product|collection|search|home|banner|hero|cta|button|image|form|field|filter|menu|nav|mobile|desktop|page|shipping|returns|price)/i.test(t);
  return !(hasNumberOrPercent && hasContextWord);
}


// --- Compatibility helpers to match older API ---
// 1) removeGarbage() – mirror older behavior using existing sanitize logic pieces
export function removeGarbage(text = '') {
  let t = String(text);

  // Strip <script>…</script>
  t = t.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '');
  // Strip HTML tags
  t = t.replace(/<\/?[^>]+>/g, '');
  // Common mojibake fixes
  t = t.replace(/[â€™â€œâ€]/g, m => ({'â€™':"'", 'â€œ':'"', 'â€':'"'}[m] || ''));
  // Bullet/numbered list leaders
  t = t.replace(/^\s*[•·▪▫◦‣⁃]\s*/gm, '');
  t = t.replace(/^\s*\d+[.)]\s*/gm, '');
  return t;
}

// 2) normalizeCTA() – keep older semantics (“action button(s)”)
export function normalizeCTA(text = '') {
  let t = String(text);
  t = t.replace(/\bCTAs?\b/gi, m => (m.toLowerCase().endsWith('s') ? 'action buttons' : 'action button'));
  t = t.replace(/\bcall[-\s]?to[-\s]?actions?\b/gi, m => (m.toLowerCase().endsWith('s') ? 'action buttons' : 'action button'));
  // De-dupe “word (word)”
  t = t.replace(/(\w+)\s*\(\s*\1\s*\)/gi, '$1');
  return t;
}

// 3) finalCleanup() – add punctuation/casing like the old version
export function finalCleanup(text = '') {
  let t = String(text);
  // Ensure sentence ends with punctuation
  t = t.replace(/([A-Za-z0-9])(\n|$)/g, (m, ch, end) => (/[.!?]$/.test(ch) ? m : ch + '.' + end));
  // Capitalize after period/start
  t = t.replace(/(^|\. )([a-z])/g, (m, p, l) => p + l.toUpperCase());
  // Collapse multi-dots and spaces
  t = t.replace(/\.{2,}/g, '.');
  t = t.replace(/\s{2,}/g, ' ');
  t = t.replace(/\n\s*\n\s*\n/g, '\n\n');
  return t.trim();
}

// 4) cleanInsightText() – keep old post-processing atop your cleanText()
export function cleanInsightText(text = '') {
  let cleaned = cleanText(text);
  cleaned = cleaned.replace(/\b(currently at|standing at|sitting at)\s+0%/gi, 'is 0%');
  cleaned = cleaned.replace(/ranging from \d+ to \d{7,} seconds/gi, 'varies significantly');
  cleaned = cleaned.replace(/\b(significant|critical|immediate|substantial) (attention|action|improvement)\b/gi, 'attention');
  return cleaned;
}

// 5) cleanMetricValue() – match older formatting rules
export function cleanMetricValue(value) {
  if (value == null || value === '') return '0';
  let cleaned = String(value).trim();

  // Percentages and decimals
  if (cleaned.includes('%') || /^0?\.\d+$/.test(cleaned)) {
    const num = parseFloat(cleaned.replace('%', ''));
    if (!Number.isFinite(num)) return '0';
    if (!cleaned.includes('%') && num <= 1) return `${Math.round(num * 100)}%`;
    return `${Math.round(num)}%`;
  }

  // Time values like "3.2 hours"
  if (/\d+\.?\d*\s*(days?|hours?|minutes?|seconds?)/i.test(cleaned)) {
    return cleaned.replace(/(\d+)\.(\d+)/, (m, whole, dec) => (parseFloat('0.' + dec) < 0.5 ? whole : m));
  }

  // Large numbers with separators
  const n = parseFloat(cleaned.replace(/,/g, ''));
  if (Number.isFinite(n) && n > 999) return n.toLocaleString();

  return cleaned;
}

export default cleanText;